import { useEffect, useState } from "react";
import { Check } from "lucide-react";
import { toast } from "sonner";
import { useStore } from "@/context/StoreContext";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

export default function MarketModal() {
  const store = useStore();
  const welcome = store.marketModal?.welcome;
  const [pendingMarket, setPendingMarket] = useState(store.market);
  const [pendingCurrency, setPendingCurrency] = useState(store.currency || "USD");

  useEffect(() => {
    if (store.marketModal?.open) {
      setPendingMarket(store.market);
      setPendingCurrency(store.currency || "USD");
    }
  }, [store.marketModal?.open, store.market, store.currency]);

  if (!store.ready) return null;

  const markets = Array.isArray(store.markets) ? store.markets : [];
  const currencyCodes = Object.keys(store.currencies || {});

  const close = () => store.closeMarketModal();

  const save = () => {
    if (!pendingMarket) return;

    const changed =
      pendingMarket !== store.market || pendingCurrency !== store.currency;

    store.setMarket(pendingMarket);
    store.setCurrency(pendingCurrency || "USD");
    close();

    if (changed && !welcome) {
      toast.success("Market updated", {
        description:
          "We'll flag any cart items that aren't available in the new market.",
      });
    }
  };

  const skip = () => {
    localStorage.setItem("zey_market_seen", "1");
    close();
  };

  return (
    <Dialog
      open={Boolean(store.marketModal?.open)}
      onOpenChange={(o) => !o && close()}
    >
      <DialogContent className="sm:max-w-lg" data-testid="market-modal">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">
            {welcome ? "Choose your market & currency" : "Switch market"}
          </DialogTitle>
          <DialogDescription>
            {welcome
              ? "Choose the region where you will redeem your products and the currency you want to see prices in."
              : "Every product lists its exact redeemable countries — eligibility comes from our catalog, not your location settings."}
          </DialogDescription>
        </DialogHeader>

        <div>
          <p className="overline-label mb-2.5">Market / Region</p>
          {markets.length > 0 ? (
            <div
              className="grid grid-cols-2 gap-2"
              data-testid="market-options"
              role="listbox"
              aria-label="Markets"
            >
              {markets.map((m) => (
                <button
                  key={m.slug}
                  data-testid={`market-option-${m.slug}`}
                  role="option"
                  aria-selected={pendingMarket === m.slug}
                  onClick={() => setPendingMarket(m.slug)}
                  className={`flex items-center gap-2.5 rounded-xl border px-3.5 py-3 text-left text-sm transition-colors duration-200 ${
                    pendingMarket === m.slug
                      ? "border-primary/60 bg-primary/10 text-foreground"
                      : "border-white/10 bg-white/[0.03] text-muted-foreground hover:border-white/25 hover:text-foreground"
                  }`}
                >
                  <span className="text-base" aria-hidden="true">
                    {m.flag}
                  </span>
                  <span className="truncate">{m.name}</span>
                  {pendingMarket === m.slug && (
                    <Check className="ml-auto h-3.5 w-3.5 shrink-0 text-primary" />
                  )}
                </button>
              ))}
            </div>
          ) : (
            <p className="rounded-xl border border-white/10 bg-white/[0.03] px-3.5 py-3 text-sm text-muted-foreground">
              Markets are temporarily unavailable. Please refresh and try again.
            </p>
          )}
        </div>

        <div className="mt-4">
          <p className="overline-label mb-2.5">Display currency</p>
          {currencyCodes.length > 0 ? (
            <div
              className="flex flex-wrap gap-1.5"
              data-testid="modal-currency-options"
            >
              {currencyCodes.map((code) => (
                <button
                  key={code}
                  data-testid={`modal-currency-${code.toLowerCase()}`}
                  onClick={() => setPendingCurrency(code)}
                  className={`rounded-full border px-3 py-1.5 font-mono text-xs transition-colors duration-200 ${
                    pendingCurrency === code
                      ? "border-primary/60 bg-primary/10 text-foreground"
                      : "border-white/10 bg-white/[0.03] text-muted-foreground hover:border-white/25 hover:text-foreground"
                  }`}
                >
                  {code}
                </button>
              ))}
            </div>
          ) : (
            <p className="rounded-xl border border-white/10 bg-white/[0.03] px-3.5 py-3 text-sm text-muted-foreground">
              Currency options are temporarily unavailable. Please refresh and try again.
            </p>
          )}
        </div>

        <div className="flex items-center justify-between pt-2">
          {welcome ? (
            <button
              data-testid="market-skip"
              onClick={skip}
              className="text-xs text-muted-foreground underline-offset-4 transition-colors duration-200 hover:text-foreground hover:underline"
            >
              Skip for now
            </button>
          ) : (
            <span />
          )}

          <Button
            size="sm"
            onClick={save}
            disabled={!pendingMarket || markets.length === 0}
            data-testid={welcome ? "market-continue" : "market-save"}
          >
            {welcome ? "Continue" : "Save selection"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
